import type {
  PostCompanyReviewExperiment2Success,
  PostCompanyReviewSuccess,
} from '@seek/ca-graphql-schema/types';
import { useFeatureFlag as useCandidateFeatureFlag } from '@seek/candidate-feature-flag';
import { useConfig } from '@seek/libs-shared';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  LAUNCH_DARKLY_FEATURE_FLAGS_MAP,
  type LaunchDarklyFeatureFlagMap,
} from '../../shared/constants/launchDarklyFeatureFlags';
import { useFeatureFlags } from '../../shared/providers/featureFlagProvider';
import { useService } from '../../shared/providers/serviceProvider';
import type {
  ConfirmedRoles,
  RoleTitle,
} from '../../shared/services/roleService/roleService';
import {
  trackWriteAReviewSubmitted,
  type ReviewFormContext,
} from '../../shared/tracking/writeAReview/trackingEvents';
import { ExperimentTwoProgress, type ReviewPayload } from '../type';
import { mapActionOrigin } from '../utils/mapActionOrigin';
import { mapReview, mapReviewExperimentTwo } from './mapper/reviewMapper';
import { WriteAReviewPage } from './WriteAReviewPage';

export const WriteAReviewContainer = () => {
  const urlSearchParams = new URL(window.location.href).searchParams;
  const companyNameQueryParam = urlSearchParams.get('cn') || undefined;
  const tracking = urlSearchParams.get('tracking') || undefined;

  const [roleTitleSessionId, setRoleTitleSessionId] = useState<string>(uuid());
  const [experimentTwoReviewId, setExperimentTwoReviewId] =
    useState<string>('');
  const [locationSessionId] = useState<string>(uuid());
  // use ReviewFormContext as state is becasue we may need to move the form to modal or drawer based form, having them integrated into the AppContext may cause redo in the future
  const reviewFormActionOrigin = mapActionOrigin(tracking);

  const [reviewFormContext, setReviewFormContext] = useState<ReviewFormContext>(
    {
      reviewId: uuid(),
      ...(reviewFormActionOrigin ? { reviewFormActionOrigin } : {}),
    },
  );

  const config = useConfig();

  const {
    roleService,
    companyProfileService,
    locationService,
    companyReviewService,
  } = useService();

  const getRoleTitles = async (query: string): Promise<RoleTitle[]> => {
    // If text is cleared, generate a new session id
    let currentSessionId = roleTitleSessionId;
    if (query === '') {
      currentSessionId = uuid();
      setRoleTitleSessionId(currentSessionId);
    }

    return await roleService.getRoleTitles(query, currentSessionId);
  };

  const searchCompanyProfiles = async (query: string) => {
    return await companyProfileService.getCompanyProfilesByName(query, true);
  };

  const searchLocations = async (query: string) => {
    return await locationService.getLocations(query, locationSessionId);
  };

  const getConfirmedRoles = async () => {
    return await roleService.getConfirmedRoles();
  };

  const updateRole = async (role: ConfirmedRoles) => {
    return await roleService.updateRole(role);
  };

  const { cookieFeatureFlags } = useFeatureFlags();

  // Experiment Two
  const reviewFormExperimentTwoFlagKey =
    LAUNCH_DARKLY_FEATURE_FLAGS_MAP['company-review-form-experiment-2'].key;
  const reviewFormExperimentTwoFlagValue = useCandidateFeatureFlag<
    LaunchDarklyFeatureFlagMap[typeof reviewFormExperimentTwoFlagKey]['values'][number]
  >(reviewFormExperimentTwoFlagKey);

  const isExperimentTwo =
    cookieFeatureFlags.EXPERIMENT_TWO_REVIEW_FLOW === true ||
    reviewFormExperimentTwoFlagValue === '3.0.0';
  const variantValueForTracking = reviewFormExperimentTwoFlagValue
    ? `schema_v${reviewFormExperimentTwoFlagValue}`
    : null;

  useEffect(() => {
    if (reviewFormExperimentTwoFlagValue) {
      setReviewFormContext((r) => ({
        ...r,
        variant: reviewFormExperimentTwoFlagValue,
      }));
    }
  }, [reviewFormExperimentTwoFlagValue]);

  const handleSubmitReview = async (review: ReviewPayload) => {
    const mappedReview = mapReview(config.language, review);

    const response = await companyReviewService.submitReview(mappedReview);

    if ((response as PostCompanyReviewSuccess).reviewId) {
      if (variantValueForTracking) {
        trackWriteAReviewSubmitted({
          config,
          context: reviewFormContext,
          variant: variantValueForTracking,
        });
      }
      return true;
    }

    return false;
  };

  const handleSubmitReviewExperimentTwo = async (
    review: ReviewPayload,
    page: ExperimentTwoProgress,
  ) => {
    const mappedReview = mapReviewExperimentTwo(review);

    const response =
      await companyReviewService.submitReviewExperimentTwo(mappedReview);

    if ((response as PostCompanyReviewExperiment2Success).reviewId) {
      setExperimentTwoReviewId(
        (response as PostCompanyReviewExperiment2Success).reviewId,
      );
      if (
        variantValueForTracking &&
        page === ExperimentTwoProgress.OverallRating
      ) {
        trackWriteAReviewSubmitted({
          config,
          context: reviewFormContext,
          variant: variantValueForTracking,
        });
      }
      return true;
    }

    return false;
  };

  return (
    <WriteAReviewPage
      getRoleTitles={getRoleTitles}
      searchCompanyProfiles={searchCompanyProfiles}
      searchLocations={searchLocations}
      companyNameQueryParam={companyNameQueryParam}
      reviewFormContext={reviewFormContext}
      getConfirmedRoles={getConfirmedRoles}
      updateRole={updateRole}
      submitReview={handleSubmitReview}
      submitReviewExperimentTwo={handleSubmitReviewExperimentTwo}
      experimentTwoReviewId={experimentTwoReviewId}
      isSimplifiedReviewForm={false} // TODO: Cleanup of Experiment One
      isExperimentTwo={isExperimentTwo}
      variantValueForTracking={variantValueForTracking}
    />
  );
};
